import {
  AnnotationURI,
  FrameSequenceURI,
  ImmutableScanBlobURI,
  PatientBlobURI,
  PhysicalArtefactURI,
  RecordingBlobURI,
  RecordingURI,
  ScanURI,
} from "@cur8/uri";
import { DateTime } from "luxon";
import { APITypesV1 } from "@cur8/api-client";
import { UnknownURI } from "./URI";
import { AuditInformation } from "./AuditInformation";

export class Box {
  x: number;
  y: number;
  w: number;
  h: number;

  constructor(x: number, y: number, w: number, h: number) {
    this.x = x;
    this.y = y;
    this.w = w;
    this.h = h;
  }

  get left() {
    return this.x;
  }

  get right() {
    return this.x + this.w;
  }

  get top() {
    return this.y;
  }

  get bottom() {
    return this.y + this.h;
  }

  intersects(box: Box) {
    return (
      this.left < box.right &&
      this.right > box.left &&
      this.top < box.bottom &&
      this.bottom > box.top
    );
  }
}

export enum AnnotationType {
  BoundingBox = "2d",
  NamedTimestamps = "namedTimestamps",
  Line = "line2d",
  PointSpace = "3d",
  Range = "range",
}

export enum AnnotationClassificationType {
  Mole = "mole",
  Other = "other",
}

export type OtherClassification = APITypesV1.OtherClassification & {
  $type: AnnotationClassificationType.Other;
};

export type MoleClassification = APITypesV1.MoleClassification & {
  $type: AnnotationClassificationType.Mole;
};

export type BoundingBoxDataType = {
  rect: Box;
};

export type Point = [number, number, number];

export type PointSpaceDataType = {
  area?: number;
  vertices: Point[];
};

export type RangeDataType = {
  range: APITypesV1.Range;
};

export type LineDataType = {
  line: APITypesV1.Line2D;
};

export type NamedTimestampsDataType = {
  namedTimestamps: APITypesV1.AnnotationDataNamedTimestamps["namedTimestamps"];
};

export type AnnotationData =
  | BoundingBoxDataType
  | NamedTimestampsDataType
  | LineDataType
  | PointSpaceDataType
  | RangeDataType
  | undefined;

export type AnnotationClassification =
  | MoleClassification
  | OtherClassification
  | undefined;

type TargetURI =
  | RecordingURI
  | AnnotationURI
  | FrameSequenceURI
  | ImmutableScanBlobURI
  | PatientBlobURI
  | RecordingBlobURI
  | PhysicalArtefactURI
  | ScanURI
  | UnknownURI;

export type Annotation<
  Classification extends AnnotationClassification = AnnotationClassification,
  Data extends AnnotationData = AnnotationData
> = {
  id: string;
  uri: AnnotationURI;
  patientId: string;
  physicalArtefactId?: string;
  targetURI: TargetURI;
  acceptState: APITypesV1.AcceptState;
  applicationSpecificTarget?: string;
  classification: Classification;
  data: Data;
  comment?: string;
  createdAt: DateTime;
  updatedAt: DateTime;
  audit: AuditInformation;
};

export type BoundingBoxAnnotation = Annotation<
  AnnotationClassification,
  BoundingBoxDataType
>;

export type LineAnnotation = Annotation<AnnotationClassification, LineDataType>;

export type NamedTimestampsAnnotation = Annotation<
  AnnotationClassification,
  NamedTimestampsDataType
>;

export type RangeAnnotation = Annotation<
  AnnotationClassification,
  RangeDataType
>;

export function hasBoundingBox(
  annotation: Annotation
): annotation is Annotation<AnnotationClassification, BoundingBoxDataType> {
  const { data } = annotation;
  if (!data) {
    return false;
  }

  return "rect" in data;
}

export function hasLine(
  annotation: Annotation
): annotation is Annotation<AnnotationClassification, LineDataType> {
  const { data } = annotation;
  if (!data) {
    return false;
  }
  return "line" in data;
}

export function hasNamedTimestamps(
  annotation: Annotation
): annotation is Annotation<AnnotationClassification, NamedTimestampsDataType> {
  const { data } = annotation;
  if (!data) {
    return false;
  }
  return "namedTimestamps" in data;
}

export function hasRange(
  annotation: Annotation
): annotation is Annotation<AnnotationClassification, RangeDataType> {
  const { data } = annotation;
  if (!data) {
    return false;
  }

  return "range" in data;
}
